import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap, WMSTileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-side-by-side';
import { LandUseAndLandCoverLayer, NDVIVegetationIndicatorData } from '../pages/Landscape';
import LeafletControlGeocoder from './custom/MapSearch';
import ExportControl from './custom/MapExportLayers';
import GeoJsonControl from './custom/MapFranceCaseStudyLayers';
import SpainControl from './custom/MapSpainLayers';
import { HistoricalMapControl } from './custom/MapFranceHisotricalLayers';
import { Radio, RadioChangeEvent } from 'antd';
import BaseMapLayerControl from './custom/MapChangeMapLayer';
import Cookies from 'js-cookie';

const opacityOptions = [
  { label: '0%', value: 0 },
  { label: '25%', value: 2.5 },
  { label: '50%', value: 5 },
  { label: '75%', value: 7.5 },
  { label: '100%', value: 10 },
];

/////////////////////////////////
//  Land Use & Land Cover Map  //
/////////////////////////////////

interface LandUseLandCoverLegendProps {
  items: { color: string; label: string }[];
  opacity: number;
  onOpacityChange: (opacity: number) => void;
}

const LandUseLandCoverLegend: React.FC<LandUseLandCoverLegendProps> = ({ items, opacity, onOpacityChange }) => {
  const handleRadioChange = (e: RadioChangeEvent) => {
    const newOpacity = Number(e.target.value); // Extract and convert to number
    onOpacityChange(newOpacity); // Call the prop with the number
  };

  return (
    <div
      id='landUseCoverMapLegendId'
      style={{
        padding: '12px',
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
        position: 'absolute',
        top: '100px',
        right: '10px',
        zIndex: 1000,
        width: '250px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Legend Title */}
      <strong style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>Layer Legend</strong>

      {/* Legend Items */}
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        {items.map((item, idx) => (
          <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                borderRadius: '3px',
                backgroundColor: item.color,
                marginRight: '10px',
                border: '1px solid #ddd',
              }}
            ></span>
            <span style={{ fontSize: '14px', color: '#333' }}>{item.label}</span>
          </li>
        ))}
      </ul>

      {/* Opacity Controls */}
      <div
        style={{
          marginTop: '12px',
          padding: '8px',
          background: '#f8f8f8',
          borderRadius: '5px',
        }}
      >
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Opacity</label>
        <Radio.Group
          options={opacityOptions}
          onChange={handleRadioChange}
          value={opacity}
          optionType='button'
          buttonStyle='solid'
        />
      </div>
    </div>
  );
};

type LandUseLandCoverMapProps = {
  layer: LandUseAndLandCoverLayer;
};

const LandUseLandCoverMap: React.FC<LandUseLandCoverMapProps> = ({ layer }) => {
  const [opacity, setOpacity] = useState(5); // Default opacity (50%)
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const caseStudy = Cookies.get('caseStudy');
  const [customGeoJsonCaseStudyControlRef, setCustomGeoJsonCaseStudyControlRef] = useState<any>(null);
  const [customSpainControlRef, setCustomSpainControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);

  const handleOpacityChange = (newOpacity: number) => {
    setOpacity(newOpacity); // Update opacity state
  };

  const SideBySideControl = () => {
    const map = useMap();
    const sideBySideControlRef = useRef<any>(null); // Store the sideBySide control instance

    useEffect(() => {
      // Remove any existing side-by-side control if it exists
      if (sideBySideControlRef.current) {
        sideBySideControlRef.current.remove();
      }

      // Create and add the layers with initial opacity
      const leftLayer = L.tileLayer
        .wms(layer.wmsUrl, {
          layers: layer.leftLayer,
          format: 'image/png',
          transparent: true,
          opacity: opacity / 10, // Set opacity based on state
        })
        .addTo(map);

      const rightLayer = L.tileLayer
        .wms(layer.wmsUrl, {
          layers: layer.rightLayer,
          format: 'image/png',
          transparent: true,
          opacity: opacity / 10, // Set opacity based on state
        })
        .addTo(map);

      // Initialize side-by-side control with the layers
      sideBySideControlRef.current = (L.control as any).sideBySide(leftLayer, rightLayer).addTo(map);
      map.flyTo(layer.center, layer.zoom);

      // Cleanup function to remove the layers and control when unmounting
      return () => {
        map.removeLayer(leftLayer);
        map.removeLayer(rightLayer);
        if (sideBySideControlRef.current) {
          sideBySideControlRef.current.remove();
        }
      };
    }, [map, layer, opacity]); // Depend on opacity to update layers when slider changes

    useEffect(() => {
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }
      if (!customGeoJsonCaseStudyControlRef) {
        const geoJsonControl = new GeoJsonControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomGeoJsonCaseStudyControlRef(geoJsonControl);
      }
      if (!customSpainControlRef && caseStudy === 'barcelona') {
        const geoJsonControl = new SpainControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomSpainControlRef(geoJsonControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
    }, [map]);

    return null;
  };

  return (
    <MapContainer
      center={layer.center}
      zoom={layer.zoom}
      style={{ height: '800px', width: '100%', borderRadius: '10px' }}
      id='landUseCoverMapId'
    >
      <WMSTileLayer
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        attribution='&copy; OpenStreetMap contributors'
      />
      <LeafletControlGeocoder />
      <SideBySideControl />

      {/* Include LandUseLandCoverLegend with opacity control */}
      <LandUseLandCoverLegend items={layer.legend} opacity={opacity} onOpacityChange={handleOpacityChange} />
    </MapContainer>
  );
};

/////////////////////////////////////
//  NDVI Vegetation Indicator Map  //
/////////////////////////////////////
type NDVIVegetationIndicatorMapProps = {
  onLayerPairSelect: (layerPair: [string, string]) => void;
  data: NDVIVegetationIndicatorData;
  legend: { color: string; label: string }[];
};

interface NDVIVegetationIndicatorLegendItem {
  color: string;
  label: string;
}

interface NDVIVegetationIndicatorLegendProps {
  items: NDVIVegetationIndicatorLegendItem[];
  onSelectPair: (pair: [number, number]) => void;
  selectedPair: [number, number] | null;
  onOpacityChange: (opacity: number) => void;
}

const NDVIVegetationIndicatorLegend: React.FC<NDVIVegetationIndicatorLegendProps> = ({
  items,
  onSelectPair,
  selectedPair,
  onOpacityChange,
}) => {
  const [opacity, setOpacity] = useState(5); // Default opacity

  const handleRadioChange = (e: RadioChangeEvent) => {
    const newOpacity = Number(e.target.value);
    setOpacity(newOpacity);
    onOpacityChange(newOpacity); // Propagate the change up to the parent component
  };

  return (
    <div
      id='ndviMapLegendId'
      style={{
        padding: '12px',
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
        position: 'absolute',
        top: '100px',
        right: '10px',
        zIndex: 1000,
        width: '270px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Legend Title */}
      <strong style={{ fontSize: '16px', display: 'block', marginBottom: '12px' }}>Layer Combinations</strong>

      {/* Layer Combinations */}
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        {items.map((item1, idx1) =>
          items.map((item2, idx2) => {
            if (idx1 < idx2) {
              const isSelected = selectedPair && selectedPair[0] === idx1 && selectedPair[1] === idx2;
              return (
                <li key={`${idx1}-${idx2}`} style={{ marginBottom: '6px' }}>
                  <button
                    onClick={() => onSelectPair([idx1, idx2])}
                    style={{
                      backgroundColor: isSelected ? '#007bff' : '#f8f9fa',
                      color: isSelected ? 'white' : '#333',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      padding: '6px 10px',
                      width: '100%',
                      textAlign: 'left',
                      transition: '0.2s ease-in-out',
                      fontSize: '14px',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#0056b3' : '#e9ecef')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#007bff' : '#f8f9fa')}
                  >
                    {item1.label} & <span style={{ marginLeft: '5px' }}>{item2.label}</span>
                  </button>
                </li>
              );
            }
            return null;
          }),
        )}
      </ul>

      {/* Opacity Controls */}
      <div
        style={{
          marginTop: '12px',
          padding: '8px',
          background: '#f8f8f8',
          borderRadius: '5px',
        }}
      >
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Opacity</label>
        <Radio.Group
          options={opacityOptions}
          onChange={handleRadioChange}
          value={opacity}
          optionType='button'
          buttonStyle='solid'
        />
      </div>
    </div>
  );
};

const NDVIVegetationIndicatorMap: React.FC<NDVIVegetationIndicatorMapProps> = ({ data, onLayerPairSelect, legend }) => {
  const [selectedPair, setSelectedPair] = useState<[number, number] | null>(null);
  const [opacity, setOpacity] = useState(5); // Default opacity
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const myLayer1Ref = useRef<L.TileLayer | null>(null);
  const myLayer2Ref = useRef<L.TileLayer | null>(null);
  const caseStudy = Cookies.get('caseStudy');
  const [customGeoJsonCaseStudyControlRef, setCustomGeoJsonCaseStudyControlRef] = useState<any>(null);
  const [customSpainControlRef, setCustomSpainControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);

  const SideBySideControl = () => {
    const map = useMap();

    useEffect(() => {
      if (selectedPair) {
        const [idx1, idx2] = selectedPair;
        let layers = data.layers;
        onLayerPairSelect([layers[idx1].title, layers[idx2].title]);

        // Fly to the new layer's center and zoom
        map.flyTo(data.center, data.zoom);

        if (myLayer1Ref.current) {
          map.removeLayer(myLayer1Ref.current);
        }
        if (myLayer2Ref.current) {
          map.removeLayer(myLayer2Ref.current);
        }

        const myLayer1 = L.tileLayer
          .wms(layers[idx1].wmsUrl, {
            layers: layers[idx1].name,
            format: 'image/png',
            transparent: true,
            opacity: opacity / 10, // Adjust opacity based on the range value
          })
          .addTo(map);

        const myLayer2 = L.tileLayer
          .wms(layers[idx2].wmsUrl, {
            layers: layers[idx2].name,
            format: 'image/png',
            transparent: true,
            opacity: opacity / 10, // Adjust opacity based on the range value
          })
          .addTo(map);

        myLayer1Ref.current = myLayer1;
        myLayer2Ref.current = myLayer2;

        const sideBySide = (L.control as any).sideBySide(myLayer1, myLayer2).addTo(map);

        return () => {
          map.removeLayer(myLayer1);
          map.removeLayer(myLayer2);
          sideBySide.remove();
        };
      }
    }, [map, selectedPair, data, opacity]);

    useEffect(() => {
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }
      if (!customGeoJsonCaseStudyControlRef) {
        const geoJsonControl = new GeoJsonControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomGeoJsonCaseStudyControlRef(geoJsonControl);
      }
      if (!customSpainControlRef && caseStudy === 'barcelona') {
        const geoJsonControl = new SpainControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomSpainControlRef(geoJsonControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
    }, []);

    return null;
  };

  return (
    <MapContainer
      center={data.center}
      zoom={data.zoom}
      style={{ height: '800px', width: '100%', borderRadius: '10px' }}
      id='ndviMapId'
    >
      <BaseMapLayerControl />
      <LeafletControlGeocoder />
      <SideBySideControl />
      <NDVIVegetationIndicatorLegend
        items={legend}
        onSelectPair={setSelectedPair}
        selectedPair={selectedPair}
        onOpacityChange={setOpacity} // Pass the setter for opacity to the legend
      />
    </MapContainer>
  );
};

export { LandUseLandCoverMap, NDVIVegetationIndicatorMap };
